import { REHYDRATE } from 'redux-persist'
import { TimeLine } from '../generated/graphql'
import {
  SnapShotProps,
  SnapshotNode,
} from '../services/snapshots/snapshot.factory'
import { TimeLineItem } from './replay.reducer'
import { convertDatesToStrings } from '../utils'

/**
 * This is the replay reducer.
 * We keep in local memory information of the timelines items loaded (raw data)
 * and the current state of the node (snapshot).
 */
export interface SnapshotState {
  // Target information
  nodeId: string

  // Timeline chunks events raw data, used by the replay context
  items: TimeLineItem[]

  // Current state use by the snapshot hook
  snapshot: SnapShotProps
}

// Define action types
export enum SnapshotActionTypes {
  INITIALIZE_SNAPSHOT = 'INITIALIZE_SNAPSHOT',
  SET_ITEMS = 'SET_ITEMS',
  SET_SNAPSHOT = 'SET_SNAPSHOT',
}

// Define the action interfaces
interface InitializeSnapshotAction {
  type: SnapshotActionTypes.INITIALIZE_SNAPSHOT
  payload: {
    nodeId: string
    tick: number
  }
}

interface SetItemsAction {
  type: SnapshotActionTypes.SET_ITEMS
  payload: TimeLineItem[]
}

interface SetSnapshotAction {
  type: SnapshotActionTypes.SET_SNAPSHOT
  payload: SnapShotProps
}

// Union type for all possible actions
export type SnapshotAction =
  | InitializeSnapshotAction
  | SetItemsAction
  | SetSnapshotAction

// Initial state
export const initialSnapshotState: SnapshotState = {
  items: [],
  nodeId: '',
  snapshot: {
    tick: -1,
    node: {} as SnapshotNode,
    index: {},
  },
}

// Reducer function
export const snapshotReducer = (
  state: SnapshotState = initialSnapshotState,
  action: SnapshotAction | { type: typeof REHYDRATE; payload: SnapshotState }
): SnapshotState => {
  switch (action.type) {
    case SnapshotActionTypes.INITIALIZE_SNAPSHOT:
      return {
        ...state,
        nodeId: action.payload.nodeId,
        items: [],
        snapshot: {
          ...initialSnapshotState.snapshot,
          tick: action.payload.tick,
        },
      }

    case SnapshotActionTypes.SET_ITEMS:
      return {
        ...state,
        items: action.payload,
      }

    case SnapshotActionTypes.SET_SNAPSHOT:
      const parsedPayload = convertDatesToStrings(action.payload)
      return {
        ...state,
        snapshot: { ...parsedPayload },
      }

    default:
      return state
  }
}

// Action creators
export const storeInitializeSnapshot = (data: {
  nodeId: string
  tick: number
}): InitializeSnapshotAction => ({
  type: SnapshotActionTypes.INITIALIZE_SNAPSHOT,
  payload: data,
})

export const storeSetItems = (items: TimeLineItem[]): SetItemsAction => ({
  type: SnapshotActionTypes.SET_ITEMS,
  payload: items,
})

export const storeSetSnapshot = (
  snapshot: SnapShotProps
): SetSnapshotAction => ({
  type: SnapshotActionTypes.SET_SNAPSHOT,
  payload: snapshot,
})
